import { computed, inject, Injectable, signal, untracked } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { StateProcess } from "@bitwarden/web-vault/app/services/store/app-state/state-process";
import {
  ProcessType,
  StateProcessFactory,
} from "@bitwarden/web-vault/app/services/store/app-state/state-process.factory";
import { interval, BehaviorSubject, combineLatest } from "rxjs";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { LoadingStateLogic } from "@bitwarden/web-vault/app/services/store/app-state/loading-state.logic";
import { AppStateType } from "@bitwarden/web-vault/app/services/store/app-state/loading-state.type";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";

/**
 * Possible context of the app. This can be improved.
 *
 * @tag with Calculation : A page or route that required any type of calculation to be loaded to be displayed correctly.
 * @tag with Data : A page that display data from the vault.
 * @tag system : Context to the app, from user perspective this is always blocking
 * @tag background : App background context, from user perspective this is never blocking
 */
export type AppContext = "calculation" | "data" | "system" | "waiting-for-user" | "background";

export const InitialAppState: AppStateType = {
  status: "idle",
  isStoreInitialised: false,
};
const DEFAULT_PROCESS_TIMEOUT_SECONDS = 60;
const PROCESS_TIMEOUT_SECONDS: Partial<Record<ProcessType, number>> = {
  "fetching-forex": 300,
  "statement-import": 300,
};

/**
 * sessionStorage key marking that a background-init failure already triggered one reload attempt in
 * this tab. Survives the reload (unlike in-memory state) so a deterministic tier-2 failure prompts
 * once and then degrades to the navigable-skeleton state instead of looping the user behind a
 * disableClose modal. Cleared once tier-2 init succeeds (see StoreLoaderService.runTier2Init).
 */
export const INIT_FAILURE_RELOAD_ATTEMPTED = "gloss.initFailureReloadAttempted";

@Injectable({
  providedIn: "root",
})
export class AppStateStoreService {
  private log: LogService = inject(LogService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private readonly initialContext: AppContext = "data";
  /** Bootstrap runs sync without overlay processes (also exposed via GlossSyncService). */
  private readonly _silentBootstrap = signal(false);
  readonly silentBootstrap = this._silentBootstrap.asReadonly();
  private loadingLogic = new LoadingStateLogic();

  /** Skipped during silent bootstrap — no overlay, no pending-state tracking. */
  private static readonly silentBootstrapProcessTypes: ReadonlySet<ProcessType> = new Set([
    "general",
    "importing-data",
    "basiq-syncing-data",
    "fiskil-syncing-data",
    "plaid-syncing-data",
    "syncing-data",
  ]);

  /** Current context of the app **/
  context = signal(this.initialContext);

  private _state = signal<AppStateType>(InitialAppState);

  state = this._state.asReadonly();

  /** App global Overlay **/
  helpBox = signal({ isVisible: false });
  wizard = signal({ isVisible: false, isCompleted: false });
  isNewLogin = signal(false);
  /** Tier 1 stores (vault decrypt + UserStore + ImportStore) done → UI unlocks. */
  isTier1Ready = signal(false);
  /** Tier 2 store initialize() calls dispatched — not async pipeline completion. */
  isTier2Ready = signal(false);
  /**
   * Tier 1 finished, tier 2 + bootstrap still running — drives skeleton placeholders.
   * Set synchronously in store init.
   */
  isBackgroundLoading = signal(false);
  /** Bumped on logout so view-level readiness latches reset for the next session. */
  loadingSession = signal(0);
  /**
   * False when the vault currently displayed is cached data we could not verify against the
   * server (offline / sync failure). Plumbing for a future offline-mode indicator and retry.
   */
  isVaultVerified = signal(true);
  /**
   * Pizza only during the INITIAL load, i.e. until the store is initialised. In that
   * window any "system"-context process blocks — the first vault fetch's "vault-loading" and
   * then "store-initialisation" — so the initial pizza is preserved. Once the store is
   * initialised we switch to skeletons and NO system process re-raises the overlay. This
   * matters for "vault-loading", which is (re)started on every subsequent sync cycle
   * (app.component "syncStarted"), including the notification-driven syncs after bootstrap;
   * gating on the "system" context alone flashed the full-screen pizza on each one
   * (bugfix/flashing-disco).
   */
  isBlockingOverlay = computed(() => {
    if (this.state().status !== "pending") {
      return false;
    }
    if (this.state().isStoreInitialised) {
      return false;
    }
    return Array.from(this._activeProcesses().values()).some(
      (p) => p.context === "system" && !p.completed(),
    );
  });

  private static readonly syncProcessTypes: ProcessType[] = [
    "plaid-syncing-data",
    "fiskil-syncing-data",
    "basiq-syncing-data",
    "importing-data",
    "syncing-data",
  ];

  /** Non-null when a sync or import is actively running — use for non-blocking sync indicators. */
  activeSyncProcess = computed((): { type: ProcessType; message: string } | null => {
    // Reactive on both state() and _activeProcesses() — either changing re-evaluates this. The
    // _activeProcesses() dependency (read below) is what tracks process start/end directly now.
    if (this.state().status !== "pending") {
      return null;
    }
    for (const type of AppStateStoreService.syncProcessTypes) {
      const p = this._activeProcesses().get(type);
      if (p && !p.completed()) {
        return { type, message: p.message };
      }
    }
    return null;
  });

  /**
   * Loading and progress bar overlay. Signal-backed (not a plain Map) so `isBlockingOverlay` /
   * `activeSyncProcess` above track it directly as a real dependency, instead of only updating
   * correctly because every mutation path also happens to push a new `state()` object (#3898).
   */
  private _activeProcesses = signal(new Map<ProcessType, StateProcess>());
  private activeProcess$ = new BehaviorSubject<StateProcess>(null);
  /** Avoid stacking multiple "refresh" dialogs if several processes exceed the timeout together. */
  private longRunningRefreshDialogPending = false;

  get activeProcesses(): ReadonlyMap<ProcessType, StateProcess> {
    // Public reactive read — templates and the isBlockingOverlay/activeSyncProcess computeds must
    // subscribe to it. Internal mutators must NOT (see currentProcesses).
    return this._activeProcesses();
  }

  /**
   * Internal untracked snapshot of the process map. Mutators read through this so a caller that
   * runs inside a reactive context — a mutator can be reached synchronously from an effect, e.g.
   * transaction.store's `flushRefreshWhenSyncUnblocks` effect → scheduleRefresh → runForexStage →
   * pullNewForexRates → forexRates.fetch → startProcess("fetching-forex") — neither subscribes to
   * `_activeProcesses` nor re-runs on every process start/end.
   */
  private currentProcesses(): Map<ProcessType, StateProcess> {
    return untracked(this._activeProcesses);
  }

  /**
   * Clones-and-sets so the signal always sees a new reference, notifying dependents. The write is
   * wrapped in `untracked` because a mutator can be reached synchronously from an effect, and on
   * this repo's pinned Angular 18.2.13 a signal write inside a reactive context throws NG0600
   * (`allowSignalWrites` defaults to false and the guard is not dev-only).
   */
  private touchActiveProcesses(): void {
    untracked(() => this._activeProcesses.set(new Map(this.currentProcesses())));
  }

  private listenerRef = combineLatest([
    this.activeProcess$.asObservable(),
    toObservable(this.context),
  ]).subscribe(() => {
    untracked(() => this.onProcessUpdate());
  });

  constructor() {
    /** Cronjob for managing rogue processes **/
    interval(3000).pipe(takeUntilDestroyed()).subscribe(this.onProcessCheck.bind(this));
  }

  clearStore() {
    this._state.set(InitialAppState);
    this.isVaultVerified.set(true);
    this.isTier1Ready.set(false);
    this.isTier2Ready.set(false);
    this.isBackgroundLoading.set(false);
    this._silentBootstrap.set(false);
    this._activeProcesses.set(new Map());
    this.loadingSession.update((n) => n + 1);
    this.activeProcess$.next(null);
    this.onProcessUpdate();
    // A new login is a new session — don't carry the one-shot reload guard across it, or a
    // different user hitting a genuine tier-2 failure on the same tab would get no prompt.
    this.clearInitFailureReloadGuard();
  }

  /** Records whether the displayed vault was confirmed against the server on the last sync. */
  setVaultVerified(verified: boolean) {
    this.isVaultVerified.set(verified);
  }

  setSilentBootstrap(silent: boolean): void {
    this._silentBootstrap.set(silent);
  }

  private onProcessUpdate() {
    const newState = this.loadingLogic.calculateNewState(
      this.context(),
      this._state(),
      this._activeProcesses(),
    );
    this._state.set(newState);
  }

  private onProcessCheck() {
    this.currentProcesses().forEach((process) => {
      const runtime = (performance.now() - process.startTime) / 1000;
      const timeout = PROCESS_TIMEOUT_SECONDS[process.type] ?? DEFAULT_PROCESS_TIMEOUT_SECONDS;
      if (runtime > timeout) {
        // Read BEFORE endProcess(): killing a "store-initialisation" terminates it, which marks it
        // completed and flips isStoreInitialised true — and a stuck initial load is exactly the
        // case that still needs the prompt.
        const wasInitialLoad = !this.state().isStoreInitialised;

        this.log.error(
          "Process " +
            process.type +
            " has been running for " +
            runtime +
            " seconds, killing process",
        );
        this.endProcess(process.type);

        // Mirror isBlockingOverlay: only prompt when the pizza was actually up, i.e.
        // during the initial load. Post-init a stalled "vault-loading" is a silent background
        // sync — an unexplained full-screen "refresh?" dialog (which reloads the app and drops
        // in-flight form/import state) is worse than a stale sync (bugfix/flashing-disco).
        const shouldPromptRefresh =
          !this.longRunningRefreshDialogPending && process.context === "system" && wasInitialLoad;
        if (shouldPromptRefresh) {
          this.longRunningRefreshDialogPending = true;
          void this.promptRefreshAfterLongRunningProcess(process.type);
        }
      } else if (runtime > 15) {
        this.log.warning(
          "Process " + process.type + " has been running for " + runtime + " seconds",
        );
        this.log.warning(JSON.stringify(process));
      }
    });
  }

  /**
   * Surfaces a terminal background-init failure with a recovery path. The tiered loader swallows
   * tier-2 throws ({@link StoreLoaderService.initialiseTier2}), and by then `isStoreInitialised`
   * has already latched true — so neither `isBlockingOverlay` nor the rogue-process watchdog
   * (both gated on `!isStoreInitialised`) will re-raise anything, yet the calc stores never
   * signalled readiness, leaving every view stuck on its skeleton for the whole session. Prompt
   * the same refresh dialog the watchdog uses — a full reload is the only in-app recovery short of
   * a vault purge. Reuses `longRunningRefreshDialogPending` so it can't stack with a
   * watchdog-raised dialog.
   *
   * One reload attempt per tab: these failures are deterministic (rxjs wiring, not transient), so a
   * second prompt would just loop the user, and the disableClose modal blocks the Settings →
   * purge-vault escape hatch. After one attempt we leave the user on the navigable-skeleton state.
   * The one-shot marker lives in sessionStorage so it survives the reload; it's cleared by
   * {@link clearInitFailureReloadGuard} when tier-2 init later succeeds.
   */
  promptRefreshAfterInitFailure(): void {
    if (this.longRunningRefreshDialogPending) {
      return;
    }
    // sessionStorage access can throw (SecurityError when storage is blocked by policy or the page
    // is framed; QuotaExceededError on write). This runs inside initialiseTier2()'s catch, whose
    // whole contract is that it must never throw (a rejection would be unhandled — PR #3708 item
    // 16), so guard it: if the one-shot marker is unreadable/unwritable, prompt anyway — one
    // unguarded loop beats rejecting the fire-and-forget caller.
    try {
      if (sessionStorage.getItem(INIT_FAILURE_RELOAD_ATTEMPTED)) {
        return;
      }
      sessionStorage.setItem(INIT_FAILURE_RELOAD_ATTEMPTED, "1");
    } catch {
      // Storage unavailable — fall through and prompt without the one-shot guarantee.
    }
    this.longRunningRefreshDialogPending = true;
    void this.promptRefreshAfterLongRunningProcess("store-initialisation");
  }

  /** Clears the one-shot reload guard so a genuinely new later failure gets its own attempt. */
  clearInitFailureReloadGuard(): void {
    // Guarded for the same reason as the setter — this also runs on the tier-2 success path, where
    // a storage throw would turn a healthy init into a spurious caught failure. A stale guard only
    // costs one missed prompt, so swallow.
    try {
      sessionStorage.removeItem(INIT_FAILURE_RELOAD_ATTEMPTED);
    } catch {
      // Ignore — a stale guard only costs one missed prompt.
    }
  }

  private async promptRefreshAfterLongRunningProcess(_processType: ProcessType): Promise<void> {
    try {
      await this.confirmationDialog.confirmFor(ConfirmationEnum.processTakingTooLongRefresh, {
        stayOpenAfterConfirmAction: () => this.reloadApplicationAfterLongRunningProcess(),
      });
    } finally {
      this.longRunningRefreshDialogPending = false;
    }
  }

  /** Full reload after user acknowledges a stuck process (policy owned here; dialog only invokes the callback). */
  private reloadApplicationAfterLongRunningProcess(): void {
    window.location.reload();
  }

  private isSilentBootstrapProcess(type: ProcessType): boolean {
    // Untracked for the same reason as currentProcesses(): startProcess is reachable synchronously
    // from an effect, and a tracked read here would subscribe that effect to the bootstrap flag —
    // it would then re-run on every setSilentBootstrap() flip. This is an imperative guard, not a
    // computed, so the value is re-read on every call and cannot go stale.
    return (
      untracked(this._silentBootstrap) && AppStateStoreService.silentBootstrapProcessTypes.has(type)
    );
  }

  startProcess(type: ProcessType, msg: string, progressMsg?: string) {
    if (this.isSilentBootstrapProcess(type)) {
      return;
    }
    this.log.info("Starting process: " + type);
    const process = StateProcessFactory.create(type);
    process.message = msg;
    process.progressMessage = progressMsg ?? "";

    if (this.currentProcesses().has(type)) {
      this.log.warning("Duplicate Running process detected:");
      this.log.warning("Old: \n" + JSON.stringify(this.currentProcesses().get(type)));
      this.log.warning("New: \n" + JSON.stringify(process));
      this.currentProcesses().get(type).merge(process);
    } else {
      this.currentProcesses().set(type, process);
    }
    this.touchActiveProcesses();

    this.activeProcess$.next(process);
  }

  /**
   * Updates the progress and messages for an active process of a given type.
   *
   * @param {ProcessType} type - The type of the process to update.
   * @param {number} progress - The progress value to set for the process, should be within the range of ***0 to 100***.
   * @param {string} [msg] - An optional message to associate with the process.
   * @param {string} [progressMsg] - An optional progress message to associate with the process.
   * @return {void} This method does not return a value.
   */
  processTick(type: ProcessType, progress: number, msg?: string, progressMsg?: string) {
    if (progress > 100) {
      this.log.warning("update:: Process cannot be greater than 100 %");
      progress = 100;
    }

    if (this.currentProcesses().has(type)) {
      /** Update map object ref **/
      const processRef = this.currentProcesses().get(type);
      processRef.tick(progress);
      processRef.message = msg ?? processRef.message;
      processRef.progressMessage = progressMsg ?? processRef.progressMessage;
      this.touchActiveProcesses();

      this.activeProcess$.next(processRef);
    } else {
      this.log.error(
        "Trying to update progress of a process that does not exist please use startProcess: " +
          type,
      );
    }
  }

  errorProcess(type: ProcessType, msg: string) {
    if (this.currentProcesses().has(type)) {
      const processRef = this.currentProcesses().get(type);
      processRef.error(msg);
      this.touchActiveProcesses();
      this.activeProcess$.next(processRef);
    } else {
      this.log.error(
        "Trying to update progress of a process that does not exist please use startProcess: " +
          type,
      );
    }
  }

  endProcess(type: ProcessType) {
    // No silent-bootstrap guard here (deliberately asymmetric with startProcess): suppressing an
    // *end* can only leak a process. A save that started before setSilentBootstrap(true) but ends
    // after it must still be removed, or it strands active in _activeProcesses (stuck "pending" /
    // "Saving data…" indicator until bootstrap clears silent or the rogue-process watchdog reaps
    // it). Ending a never-started (silent-suppressed) process is a benign no-op via the else branch
    // below. See PR #3708 items 11 & 14.
    if (this.currentProcesses().has(type)) {
      const processRef = this.currentProcesses().get(type);

      this.log.info(
        "Ending process: " +
          type +
          " Runtime : " +
          (performance.now() - processRef.startTime) / 1000,
      );
      processRef.terminate();

      // Emit while the just-completed process is still in the map. activeProcess$ is the sole
      // trigger for onProcessUpdate()/calculateNewState, and the isStoreInitialised latch is set
      // only by scanning the map for a completed 'store-initialisation'. Emitting after the delete
      // would run that scan on a map that no longer holds it → latch never flips (dead bootstrap).
      this.activeProcess$.next(null);

      if (processRef.completed()) {
        this.currentProcesses().delete(type);
      }
      this.touchActiveProcesses();
    } else {
      // No-op by design: ending an already-ended / never-started process is benign — e.g. an
      // error-path endProcess() after a paired finally already ended it, or an endProcess() reached
      // before its startProcess() ran (sync catch blocks). Kept at debug level so it doesn't trip
      // APM/error alerting on the shared sync paths. See PR #3708.
      this.log.debug("endProcess called for a process that is not active (no-op): " + type);
    }
  }
}
